import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Tall enough for the status list (placeholder + every status) without an inner scrollbar. */
const MENU_MAX_PX = 360;
const MENU_GAP_PX = 4;

/**
 * Custom dropdown with search; fires native-like change events for form handlers.
 * Pass `multiple` for multi-select (value = string[]).
 */
export function SearchableSelect({
  name,
  value,
  onChange,
  options,
  placeholder = 'Select',
  disabled = false,
  className = '',
  /** When false, hide the empty placeholder row (required filters). */
  clearable = true,
  multiple = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);

  const selectedValues = useMemo(() => {
    if (multiple) {
      if (Array.isArray(value)) return value.map(String).filter((v) => v !== '');
      if (value == null || value === '') return [];
      return [String(value)];
    }
    return value != null && value !== '' ? [String(value)] : [];
  }, [multiple, value]);

  const selectedLabels = useMemo(() => {
    return selectedValues
      .map((v) => options.find((o) => String(o.value) === v)?.label)
      .filter(Boolean);
  }, [options, selectedValues]);

  const displayLabel = multiple
    ? selectedLabels.length
      ? selectedLabels.join(', ')
      : ''
    : selectedLabels[0] ?? '';

  const hasOptions = options.length > 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        String(o.label).toLowerCase().includes(q) ||
        String(o.value).toLowerCase().includes(q)
    );
  }, [options, query]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }

    const update = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP_PX;
      const spaceAbove = rect.top - MENU_GAP_PX;
      const openUp = spaceBelow < MENU_MAX_PX && spaceAbove > spaceBelow;
      const space = openUp ? spaceAbove : spaceBelow;
      const searchPx = hasOptions ? 41 : 0;
      const next = {
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 80,
        listMaxHeight: Math.max(160, Math.min(MENU_MAX_PX, space) - searchPx),
        ...(openUp
          ? { top: 'auto', bottom: window.innerHeight - rect.top + MENU_GAP_PX }
          : { top: rect.bottom + MENU_GAP_PX, bottom: 'auto' }),
      };
      setMenuStyle((prev) => {
        if (
          prev &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.listMaxHeight === next.listMaxHeight &&
          prev.top === next.top &&
          prev.bottom === next.bottom
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    const onScroll = (e) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      update();
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, filtered.length, hasOptions]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const dispatchChange = (newValue) => {
    onChange({
      target: {
        name,
        value: newValue,
        type: multiple ? 'select-multiple' : 'select-one',
      },
    });
    if (!multiple) {
      setOpen(false);
      setQuery('');
    }
  };

  const toggleMultiValue = (optionValue) => {
    const next = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];
    dispatchChange(next);
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`${className} flex w-full cursor-pointer items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`min-w-0 flex-1 truncate ${displayLabel ? '' : 'text-gray-500'}`}>
          {displayLabel || placeholder}
        </span>
        <svg
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && !disabled && menuStyle
        ? createPortal(
        <div
          ref={menuRef}
          style={{
            position: menuStyle.position,
            left: menuStyle.left,
            width: menuStyle.width,
            top: menuStyle.top,
            bottom: menuStyle.bottom,
            zIndex: menuStyle.zIndex,
          }}
          className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
        >
          {hasOptions && (
            <input
              type="text"
              className="w-full shrink-0 border-b border-gray-100 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              autoComplete="off"
              autoFocus
            />
          )}
          <ul
            className="overflow-y-auto py-1"
            style={{ maxHeight: menuStyle.listMaxHeight }}
            role="listbox"
            aria-multiselectable={multiple}
          >
            {clearable && (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedValues.length === 0}
                  className="w-full px-3 py-2 text-left text-sm text-gray-600 hover:bg-amber-50"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => dispatchChange(multiple ? [] : '')}
                >
                  {placeholder}
                </button>
              </li>
            )}
            {query.trim() !== '' && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-500">No matches</li>
            )}
            {filtered.map((o, i) => {
              const optionValue = String(o.value);
              const isSelected = selectedValues.includes(optionValue);
              return (
                <li key={`${optionValue}-${i}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-amber-50 ${
                      isSelected ? 'bg-brand/15 font-medium text-gray-900' : 'text-gray-900'
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() =>
                      multiple ? toggleMultiValue(optionValue) : dispatchChange(optionValue)
                    }
                  >
                    {multiple ? (
                      <span
                        className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          isSelected
                            ? 'border-brand bg-brand text-white'
                            : 'border-gray-300 bg-white'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected ? '✓' : ''}
                      </span>
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
        document.body
      )
        : null}
    </div>
  );
}
